import { computed, inject, Injectable } from '@angular/core';
import { PolicyGridStore } from '@program-suite/data-common';
import { LookupStore } from '@program-suite/data-common';
import { AuthStore } from '@program-suite/data-auth';
import { TPolicyGridRowViewModel } from '@program-suite/data-common/program';
import { GridOptions } from '@program-suite/ui-grid';

@Injectable({
  providedIn: 'root',
})
export class PolicyGridOptions {
  lookupStore = inject(LookupStore);
  policyGridStore = inject(PolicyGridStore);
  authStore = inject(AuthStore);
  hideStateForPortal = this.authStore.portal() !== 'program';

  totalNumberOfItems = computed<number>(() =>
    this.policyGridStore.resource.value() ? this.policyGridStore.resource.value()?.totalCount ?? 0 : 0
  );

  data = computed<TPolicyGridRowViewModel[] >(() =>
    this.policyGridStore.resource.value() ? this.policyGridStore.resource.value()?.rows ?? [] : []
  );
AdminOptions = <GridOptions>{
  filterData: [
    { name: 'From', formControlName: 'dateFrom', type: 'date', dateOffset: -365 },
    { name: 'To', formControlName: 'dateTo', type: 'date', dateOffset: 90 },
    { name: 'Search', formControlName: 'searchTerm', type: 'search' },
    { name: 'Type', formControlName: 'typeName', type: 'dropdown', lookup: this.lookupStore.submissionTypes },
    { name: 'Status', formControlName: 'statusId', type: 'dropdown', lookup: this.lookupStore.policyStatuses },
    { name: 'State', formControlName: 'stateCode', type: 'dropdown', lookup: this.lookupStore.states, hideFilter: this.hideStateForPortal },
    { name: 'Program', formControlName: 'programId', type: 'dropdown', lookup: this.lookupStore.programs },
    { name: 'Tenant', formControlName: 'tenantId', type: 'dropdown', lookup: this.lookupStore.tenants },
    { name: 'Agency', formControlName: 'agencyId', type: 'dropdown', lookup: this.lookupStore.agencies, dependencies: ['tenantId'] },
    { name: 'Producer', formControlName: 'producerId', type: 'dropdown', lookup: this.lookupStore.agents, dependencies: ['agencyId', 'tenantId'] },
  ],
  filterValues: this.policyGridStore.filterValues,
  openFilters: true,
  columns: [
    { header: 'Effective Date', controlName: 'policyEffectiveDate', displayPipe: 'date'},
    { header: 'Tenant', controlName: 'tenantName'},
    { header: 'Type', controlName: 'type'},
    { header: 'Status', controlName: 'status'},
    { header: 'Policy #', controlName: 'policyNumber', minWidth: '100px'},
    { header: 'Submission #', controlName: 'submissionNumber', minWidth: '100px'},
    { header: 'Program', controlName: 'program'},
    { header: 'State', controlName: 'state', hideColumn: this.hideStateForPortal },
    { header: 'Business Name', controlName: 'businessName'},
    { header: 'Contact Name', controlName: 'contactName', hideColumnSort: true},
    { header: 'Agent/Producer', controlName: 'producerName'},
    { header: 'Last Updated', controlName: 'lastUpdated', displayPipe: 'date'},
    { header: 'Manage', controlName: 'action', 
      actionColumnitems: [
        { type: 'icon', buttonAction: 'action',
          conditionalDisplay: { calculateDisplay: () => 'eye', column: 'status' },
         },
      ]
    }
  ],
  data: this.data,
  totalNumberOfItems: this.totalNumberOfItems,
  frontEndPagination: false,
  fetchData: false,
  displaySearch: false,
};

WholesalerOptions = <GridOptions>{
  filterData: [
    { name: 'From', formControlName: 'dateFrom', type: 'date', dateOffset: -365 },
    { name: 'To', formControlName: 'dateTo', type: 'date', dateOffset: 90 },
    { name: 'Search', formControlName: 'searchTerm', type: 'search' },
    { name: 'Type', formControlName: 'typeName', type: 'dropdown', lookup: this.lookupStore.submissionTypes },
    { name: 'Status', formControlName: 'statusId', type: 'dropdown', lookup: this.lookupStore.policyStatuses },
    { name: 'State', formControlName: 'stateCode', type: 'dropdown', lookup: this.lookupStore.states, hideFilter: this.hideStateForPortal },
    { name: 'Program', formControlName: 'programId', type: 'dropdown', lookup: this.lookupStore.programs },
    { name: 'Agency', formControlName: 'agencyId', type: 'dropdown', lookup: this.lookupStore.agencies },
    { name: 'Producer', formControlName: 'producerId', type: 'dropdown', lookup: this.lookupStore.agents, dependencies: ['agencyId']  },
  ],
  filterValues: this.policyGridStore.filterValues,
  openFilters: true,
  columns: [
    { header: 'Effective Date', controlName: 'policyEffectiveDate', displayPipe: 'date'},
    { header: 'Type', controlName: 'type'},
    { header: 'Status', controlName: 'status'},
    { header: 'Policy #', controlName: 'policyNumber', minWidth: '100px'},
    { header: 'Submission #', controlName: 'submissionNumber', minWidth: '100px'},
    { header: 'Program', controlName: 'program'},
    { header: 'State', controlName: 'state', hideColumn: this.hideStateForPortal },
    { header: 'Business Name', controlName: 'businessName'},
    { header: 'Contact Name', controlName: 'contactName', hideColumnSort: true},
    { header: 'Agent/Producer', controlName: 'producerName'},
    { header: 'Last Updated', controlName: 'lastUpdated', displayPipe: 'date'},
    { header: 'Manage', controlName: 'action', 
      actionColumnitems: [
        { type: 'icon', buttonAction: 'action',
          conditionalDisplay: { calculateDisplay: () => 'eye', column: 'status' },
         },
      ]
    }
  ],
  data: this.data,
  totalNumberOfItems: this.totalNumberOfItems,
  frontEndPagination: false,
  fetchData: false,
  displaySearch: false,
};

// LossRunProcessor: basic filters only (no tenant/agency/producer lookups)
LossRunProcessorOptions = <GridOptions>{
  filterData: [
    { name: 'From', formControlName: 'dateFrom', type: 'date', dateOffset: -365 },
    { name: 'To', formControlName: 'dateTo', type: 'date', dateOffset: 90 },
    { name: 'Search', formControlName: 'searchTerm', type: 'search' },
    { name: 'Type', formControlName: 'typeName', type: 'dropdown', lookup: this.lookupStore.submissionTypes },
    { name: 'Status', formControlName: 'statusId', type: 'dropdown', lookup: this.lookupStore.policyStatuses },
    { name: 'Program', formControlName: 'programId', type: 'dropdown', lookup: this.lookupStore.programs },
  ],
  filterValues: this.policyGridStore.filterValues,
  openFilters: true,
  columns: [
    { header: 'Effective Date', controlName: 'policyEffectiveDate', displayPipe: 'date'},
    { header: 'Tenant', controlName: 'tenantName'},
    { header: 'Type', controlName: 'type'},
    { header: 'Status', controlName: 'status'},
    { header: 'Policy #', controlName: 'policyNumber', minWidth: '100px'},
    { header: 'Submission #', controlName: 'submissionNumber', minWidth: '100px'},
    { header: 'Program', controlName: 'program'},
    { header: 'Business Name', controlName: 'businessName'},
    { header: 'Contact Name', controlName: 'contactName', hideColumnSort: true},
    { header: 'Agent/Producer', controlName: 'producerName'},
    { header: 'Last Updated', controlName: 'lastUpdated', displayPipe: 'date'},
    { header: 'Manage', controlName: 'action',
      actionColumnitems: [
        { type: 'icon', buttonAction: 'action',
          conditionalDisplay: { calculateDisplay: () => 'eye', column: 'status' },
         },
      ]
    }
  ],
  data: this.data,
  totalNumberOfItems: this.totalNumberOfItems,
  frontEndPagination: false,
  fetchData: false,
  displaySearch: false,
};

OtherOptions = <GridOptions>{
  filterData: [
    { name: 'From', formControlName: 'dateFrom', type: 'date', dateOffset: -365 },
    { name: 'To', formControlName: 'dateTo', type: 'date', dateOffset: 90 },
    { name: 'Search', formControlName: 'searchTerm', type: 'search' },
    { name: 'Type', formControlName: 'typeName', type: 'dropdown', lookup: this.lookupStore.submissionTypes },
    { name: 'Status', formControlName: 'statusId', type: 'dropdown', lookup: this.lookupStore.policyStatuses },
    { name: 'State', formControlName: 'stateCode', type: 'dropdown', lookup: this.lookupStore.states, hideFilter: this.hideStateForPortal },
    { name: 'Program', formControlName: 'programId', type: 'dropdown', lookup: this.lookupStore.programs },
    { name: 'Producer', formControlName: 'producerId', type: 'dropdown', lookup: this.lookupStore.agents },
  ],
  filterValues: this.policyGridStore.filterValues,
  openFilters: true,
  columns: [
    { header: 'Effective Date', controlName: 'policyEffectiveDate', displayPipe: 'date'},
    { header: 'Type', controlName: 'type'},
    { header: 'Status', controlName: 'status'},
    { header: 'Policy #', controlName: 'policyNumber', minWidth: '100px'},
    { header: 'Submission #', controlName: 'submissionNumber', minWidth: '100px'},
    { header: 'Program', controlName: 'program'},
    { header: 'State', controlName: 'state', hideColumn: this.hideStateForPortal },
    { header: 'Business Name', controlName: 'businessName'},
    { header: 'Contact Name', controlName: 'contactName', hideColumnSort: true},
    { header: 'Agent/Producer', controlName: 'producerName'},
    { header: 'Last Updated', controlName: 'lastUpdated', displayPipe: 'date'},
    { header: 'Manage', controlName: 'action', 
      actionColumnitems: [
        { type: 'icon', buttonAction: 'action',
          conditionalDisplay: { calculateDisplay: () => 'eye', column: 'status' },
         },
      ]
    }
  ],
  data: this.data,
  totalNumberOfItems: this.totalNumberOfItems,
  frontEndPagination: false,
  fetchData: false,
  displaySearch: false,
};
}